import { getEarliestDeparture } from "@/helpers/loanDepartures";
import { test } from "@/requests/loanRequests";
import { get } from "@/requests/server";
import dayjs from "dayjs";
import i18n from "@/i18n";

// Cache for 5 minutes
const loanablesCacheTTL = 5;

export function getInitialState() {
  return {
    center: null,
    selectedLoanableTypes: [],
    searchDepartureAt: null,
    searchDuration: null,
    estimatedDistance: null,
    selectedLoanable: null,
    newLoanModalOpened: false,
    loanablesByType: {},
    loanableEstimates: {},
    loading: false,
    lastTypeFetchDate: null,
    availableLoanableIds: [],
    availabilitiesLoading: false,
    loanablesLoading: false,
    testingLoanableIds: [],
    testedLoanableIds: [],
  };
}

export function persistState(state) {
  // Do not persist loading state, availability and
  // invalidate the cache (lastTypeFetchDate not set).
  return {
    estimatedDistance: state.estimatedDistance,
    loanablesByType: state.loanablesByType,
    searchDepartureAt: state.searchDepartureAt,
    searchDuration: state.searchDuration,
    selectedLoanableTypes: state.selectedLoanableTypes,
  };
}

export default {
  namespaced: true,
  state: getInitialState(),
  mutations: {
    availbilitiesLoading(state, value) {
      state.availabilitiesLoading = value;
    },
    center(state, center) {
      state.center = center;
    },
    estimatedDistance(state, value) {
      state.estimatedDistance = value;
      state.loanableEstimates = {};
    },
    invalidateCache(state) {
      state.lastTypeFetchDate = null;
    },
    loanablesLoading(state, value) {
      state.loanablesLoading = value;
    },
    mergeLoanablesByType(state, loanableByTypes) {
      state.lastTypeFetchDate = new dayjs().toISOString();
      state.loanablesByType = { ...state.loanablesByType, ...loanableByTypes };
    },
    newLoanModalOpened(state, value) {
      state.newLoanModalOpened = value;
    },
    reset(state) {
      Object.assign(state, getInitialState());
    },
    resetEstimates(state) {
      state.loanableEstimates = {};
    },
    searchDepartureAt(state, date) {
      let dayjsDate = dayjs(date);

      let oldDate = state.searchDepartureAt;
      if (dayjsDate.isValid()) {
        state.searchDepartureAt = dayjsDate.format("YYYY-MM-DD HH:mm");
      } else {
        state.searchDepartureAt = getEarliestDeparture().format("YYYY-MM-DD HH:mm");
      }

      if (oldDate === state.searchDepartureAt) {
        return;
      }

      state.availableLoanableIds = [];
      state.loanableEstimates = {};
      state.testedLoanableIds = [];
      state.testingLoanableIds = [];
    },
    searchDuration(state, duration) {
      let oldDuration = state.searchDuration;
      if (duration && duration > 15) {
        state.searchDuration = duration;
      } else {
        state.searchDuration = 15;
      }

      if (oldDuration === state.searchDuration) {
        return;
      }

      state.availableLoanableIds = [];
      state.loanableEstimates = {};
      state.testedLoanableIds = [];
      state.testingLoanableIds = [];
    },
    selectedLoanable(state, loanable) {
      state.selectedLoanable = loanable;
    },
    selectedLoanableTypes(state, selectedLoanableTypes) {
      state.selectedLoanableTypes = selectedLoanableTypes;
    },
    setLoanableAvailable(state, { id, available }) {
      const previouslyAvailable = state.availableLoanableIds.includes(id);
      if (available && !previouslyAvailable) {
        state.availableLoanableIds.push(id);
        return;
      }

      if (!available && previouslyAvailable) {
        state.availableLoanableIds = state.availableLoanableIds.filter((i) => i !== id);
      }
    },
    setLoanableEstimate(state, { id, estimate }) {
      state.loanableEstimates[id] = estimate;
    },
    setLoanableTested(state, loanableId) {
      state.testingLoanableIds = state.testingLoanableIds.filter((id) => id !== loanableId);
      if (!state.testedLoanableIds.includes(loanableId)) {
        state.testedLoanableIds.push(loanableId);
      }
    },
    setTestingLoanable(state, loanableId) {
      state.testingLoanableIds.push(loanableId);
    },
    setTestedLoanableIds(state, loanableIds) {
      state.testedLoanableIds = loanableIds;
    },
    updateAvailabilities(state, availableLoanableIds) {
      state.testingLoanableIds = [];
      state.availableLoanableIds = availableLoanableIds;
    },
    updateEstimates(state, estimates) {
      estimates.reduce((acc, r) => {
        acc[r.id] = r.estimate;
        return acc;
      }, state.loanableEstimates);
    },
  },
  actions: {
    async list({ commit, state }, { types }) {
      commit("selectedLoanableTypes", types);

      if (!types || types.length === 0) {
        return;
      }

      // Check if cached loanables are enough
      if (
        state.lastTypeFetchDate &&
        new dayjs().diff(state.lastTypeFetchDate, "minutes") < loanablesCacheTTL
      ) {
        return;
      }

      commit("loanablesLoading", true);
      const { data } = await get("/loanables/listByType", {
        notifications: { action: i18n.t("store.pages.loanable.search.notifications.list.action") },
        cleanupCallback: () => commit("loanablesLoading", false),
      });
      commit("mergeLoanablesByType", data);
    },
    async testAll({ commit, state, dispatch, getters }, ids) {
      // We ensure the shown loanables are at most loanablesCacheTTL minutes old
      // when searching for available loanables
      // This can be async since both available loanable ids and the loanable list are independent
      dispatch("list", { types: state.selectedLoanableTypes });

      // if last search date below the minimum, no loanables will be available.
      // Move it to the minimum time.
      let earliestDeparture = getEarliestDeparture();
      if (earliestDeparture.isAfter(state.searchDepartureAt)) {
        commit("searchDepartureAt", earliestDeparture);
      }

      const params = {
        departure_at: state.searchDepartureAt,
        duration_in_minutes: state.searchDuration,
        estimated_distance: state.estimatedDistance,
        ids: (ids ?? []).join(","),
      };

      commit("availbilitiesLoading", true);
      const { data } = await get(`/loanables/search`, {
        axiosRequestConfig: { params },
        notifications: {
          action: i18n.t("store.pages.loanable.search.notifications.test_all.action"),
        },
        cleanupCallback: () => commit("availbilitiesLoading", false),
      });

      commit(
        "updateAvailabilities",
        data.filter((r) => r.available).map((r) => r.id)
      );
      commit("updateEstimates", data);
      commit("setTestedLoanableIds", ids ?? getters.loanables.map((l) => l.id));
    },
    async testOne({ commit, state }, { loanable_id, borrower_user_id }) {
      commit("setTestingLoanable", loanable_id);

      const data = await test({
        departure_at: state.searchDepartureAt,
        duration_in_minutes: state.searchDuration,
        estimated_distance: state.estimatedDistance ?? 10,
        loanable_id,
        borrower_user_id,
      });
      if (!data) {
        return;
      }

      commit("setLoanableTested", loanable_id);
      commit("setLoanableAvailable", { id: loanable_id, available: data.available });
      commit("setLoanableEstimate", {
        id: loanable_id,
        estimate: data.borrower_invoice?.user_balance_change ?? 0,
      });
    },
  },
  getters: {
    allTested(state) {
      return state.loanables.map((l) => l.id).filter((id) => state.testedLoanableIds.includes(id));
    },
    estimatedDistance(state) {
      return state.estimatedDistance;
    },
    loading(state) {
      return state.loanablesLoading || state.availabilitiesLoading;
    },
    loanables(state) {
      const loanables = [];
      const availableLoanableIds = new Set(state.availableLoanableIds);
      const testedLoanableIds = new Set(state.testedLoanableIds);
      const testingLoanableIds = new Set(state.testingLoanableIds);

      for (const loanableType of state.selectedLoanableTypes) {
        loanables.push(
          ...(state.loanablesByType[loanableType] || [])
            .filter((loanable) => loanable.position_google)
            .map((loanable) => ({
              ...loanable,
              available: availableLoanableIds.has(loanable.id),
              estimated: state.loanableEstimates[loanable.id] !== undefined,
              tested: testedLoanableIds.has(loanable.id),
              testing: testingLoanableIds.has(loanable.id),
            }))
        );
      }

      loanables.sort((a, b) => a.name.localeCompare(b.name));

      return loanables;
    },
  },
};
